import {
  Button,
  Divider,
  Dropdown,
  InlineBanner,
  ListItem,
  Sidebar,
  Spinner,
  VerticalNav,
} from '@bynder/design-system';
import { IconAdd } from '@bynder/icons';
import { AudiencePage } from './ExperimentalSubPageMenu';
import { usePageTemplateModal } from '../../../hooks/useModals';
import { generateUUID } from '../../../helpers/utils';
import { usePageMutations } from '../../../mutations/pages';
import AudiencePagesList from './ExperimentalSubPageMenu';
import { Guide } from '../../../types';
import styled from 'styled-components';
import { useIsEntityChanging } from '../../../stores/socketsStore';
import { EntityEnum } from '../../modals/NewCreateModal';
import { useTranslate, Translate } from '@bynder/localization';
import { useActivePage } from '../../../queries/guides';

const AudiencesSidebar: React.FC<{ guide: Guide }> = ({ guide }) => {
  const pages = guide.chapters[0].pages;
  const hasOnePage = pages.length === 1;
  const lastPage = pages[pages.length - 1];
  return (
    <SidebarContainer>
      <VerticalNav>
        <ListItem>
          <strong>
            <Translate id="AUDIENCES_MODAL.DEFAULT_HOMEPAGE_SUBTEXT" />
          </strong>
        </ListItem>
        <AudiencePage page={lastPage} isLast />
        <Divider />
        <ListItem rightElements={<AudiencesDropdown guide={guide} />}>
          <strong>Targeted pages</strong>
        </ListItem>
        <AudiencePagesList pages={pages} />
        {hasOnePage && <Banner />}
      </VerticalNav>
    </SidebarContainer>
  );
};

const SidebarContainer = styled(Sidebar)`
  min-width: 272px;
`;

export default AudiencesSidebar;

const Banner = () => {
  return (
    <InlineBanner variant="info" link={<a href="#">Learn more</a>}>
      Show the right content to the right users—personalize your homepage with
      targeted pages.
    </InlineBanner>
  );
};

const AudiencesDropdown: React.FC<{ guide: Guide }> = ({ guide }) => {
  const { translate } = useTranslate();
  const displayPageTemplateModal = usePageTemplateModal();
  const { data: activePage } = useActivePage();
  const { duplicatePage } = usePageMutations();
  const toPageId = generateUUID();
  const isChangingEntity = useIsEntityChanging(guide.id);

  const toIndex = guide.chapters[0].pages.length - 1;
  const addSubPage = () => {
    displayPageTemplateModal({
      chapterId: guide.chapters[0].id,
      toIndex,
      entityType: EntityEnum.AUDIENCE_PAGE,
    });
  };

  const handleDuplicatePage = () => {
    const defaultHomepage =
      guide.chapters[0].pages[guide.chapters[0].pages.length - 1];
    const isLastHomepagePageActive = activePage.id === defaultHomepage.id;
    // If last page is active, copy guide title, otherwise copy page title
    const guideTitle = isLastHomepagePageActive
      ? guide.title + translate('DUPLICATE_PAGE_MODAL.COPY')
      : activePage.title + translate('DUPLICATE_PAGE_MODAL.COPY');
    duplicatePage.mutate({
      toGuideId: guide.id,
      toChapterId: guide.chapters[0].id,
      toPageId,
      toGuideVersion: guide.version,
      guideTitle,
      toIndex,
    });
  };

  return (
    <Dropdown
      trigger={({ ...triggerProps }) => (
        <Button
          aria-label="Add page"
          variant="clean"
          icon={isChangingEntity ? <Spinner /> : <IconAdd />}
          isDisabled={isChangingEntity}
          {...triggerProps}
        />
      )}
    >
      <Dropdown.Item onClick={handleDuplicatePage}>
        Duplicate this page
      </Dropdown.Item>
      <Dropdown.Item onClick={addSubPage}>Start a new page</Dropdown.Item>
    </Dropdown>
  );
};
